/**
 * `FileSource` and `FileSink` — the protocol's I/O boundaries, injectable.
 *
 * The KEY point of memory: the sender reads the file in WINDOWS
 * (`source.slice(start, end)`), never whole. A browser `Blob`/`File` satisfies
 * this natively (`blob.slice(a,b).arrayBuffer()` reads only that window
 * from disk). The receiver writes in windows (`sink.write(offset, bytes)`) and
 * keeps track of `bytesReceived` to be able to resume after a cut.
 *
 * In tests the in-memory variants are used; in the browser, the `Blob`
 * adapter and a sink that accumulates (or, in the documented improvement, the File System
 * Access API to avoid accumulating in RAM).
 */

export interface FileSource {
  readonly name: string;
  readonly size: number;
  readonly mime: string;
  /** Reads the window [start, end) WITHOUT loading the whole file. */
  slice: (start: number, end: number) => Promise<Uint8Array>;
}

export interface FileSink {
  /** Writes `bytes` at `offset`. Must be idempotent per offset (resumption). */
  write: (offset: number, bytes: Uint8Array) => Promise<void> | void;
  /** Contiguous bytes already received from 0 (resumption point). */
  bytesReceived: () => number;
  /**
   * Returns the complete assembled file for the final SHA-256 verification
   * (includes the resumed prefix of a previous session). If the sink cannot
   * re-read (e.g. it writes directly to disk without a buffer), it is omitted and
   * integrity falls back on the per-chunk CRC32.
   */
  readAll?: () => Promise<Uint8Array> | Uint8Array;
  /** Closes the file (flush). Optional. */
  close?: () => Promise<void> | void;
}

/** `FileSource` over a browser `Blob`/`File` (real window reads). */
export function blobSource(name: string, blob: Blob, mime?: string): FileSource {
  return {
    name,
    size: blob.size,
    mime: mime ?? blob.type ?? "application/octet-stream",
    async slice(start, end) {
      const buffer = await blob.slice(start, end).arrayBuffer();
      return new Uint8Array(buffer);
    },
  };
}

/** In-memory `FileSource` (tests). */
export function memorySource(
  name: string,
  data: Uint8Array,
  mime = "application/octet-stream",
): FileSource {
  return {
    name,
    size: data.length,
    mime,
    slice(start, end) {
      return Promise.resolve(data.slice(start, end));
    },
  };
}

/**
 * In-memory `FileSink` that supports resumption. Reserves the buffer of the known
 * size and marks the written ranges; `bytesReceived` returns the contiguous
 * prefix (which allows requesting resumption from the first gap).
 */
export function memorySink(size: number): FileSink & { data: () => Uint8Array } {
  const buffer = new Uint8Array(size);
  const written: { start: number; end: number }[] = [];

  function contiguous(): number {
    let end = 0;
    let advanced = true;
    while (advanced) {
      advanced = false;
      for (const seg of written) {
        if (seg.start <= end && seg.end > end) {
          ({ end } = seg);
          advanced = true;
        }
      }
    }
    return Math.min(end, size);
  }

  return {
    write(offset, bytes) {
      buffer.set(bytes.subarray(0, Math.max(0, Math.min(bytes.length, size - offset))), offset);
      written.push({ start: offset, end: offset + bytes.length });
    },
    bytesReceived() {
      return contiguous();
    },
    readAll() {
      return buffer;
    },
    data() {
      return buffer;
    },
  };
}
